import React from "react";

import { AlertCircle } from "lucide-react";

interface ErrorStateProps {
  title?: string;
  message?: string;
  variant?: "warning" | "error";
  className?: string;
  children?: React.ReactNode;
}

const VARIANT_STYLES = {
  warning: {
    container:
      "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200",
    icon: "text-amber-500 dark:text-amber-400",
    title: "text-amber-800 dark:text-amber-200",
    message: "text-amber-700 dark:text-amber-300",
  },
  error: {
    container:
      "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200",
    icon: "text-red-500 dark:text-red-400",
    title: "text-red-800 dark:text-red-200",
    message: "text-red-700 dark:text-red-300",
  },
};

export const ErrorState = React.memo(function ErrorState({
  title = "Error",
  message,
  variant = "error",
  className,
  children,
}: ErrorStateProps) {
  const styles = VARIANT_STYLES[variant];
  const containerClassName =
    className || `${styles.container} rounded-lg p-4 border`;

  return (
    <div className={containerClassName}>
      <div className="flex items-start gap-3">
        <AlertCircle
          className={`w-5 h-5 ${styles.icon} mt-0.5 flex-shrink-0`}
        />
        <div className="space-y-2 flex-1">
          <p className={`text-sm font-medium ${styles.title}`}>{title}</p>
          {message && (
            <p
              className={`text-sm ${styles.message} leading-relaxed whitespace-pre-wrap break-all line-clamp-2`}
            >
              {message}
            </p>
          )}
          {children}
        </div>
      </div>
    </div>
  );
});
